/**
 * i18n Configuration — English + Hindi
 * Stores selected language in localStorage. Default: English.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const resources = {
  en: {
    translation: {
      // Navigation
      calendar: 'Calendar',
      analytics: 'Analytics',
      profile: 'Profile',
      settings: 'Settings',
      // Booking
      bookings: 'Bookings',
      addBooking: 'Add Booking',
      newBooking: 'New Booking',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      guestName: 'Guest Name',
      roomType: 'Room Type',
      amount: 'Amount',
      status: 'Status',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
      checkedIn: 'Checked In',
      checkedOut: 'Checked Out',
      pending: 'Pending',
      // Actions
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      retry: 'Retry',
      close: 'Close',
      logout: 'Logout',
      login: 'Login',
      // Dashboard / Analytics
      totalBookings: 'Total Bookings',
      totalRevenue: 'Total Revenue',
      otaBookings: 'OTA Bookings',
      walkInBookings: 'Walk-in',
      cancelledBookings: 'Cancelled',
      averageValue: 'Average Value',
      revenue: 'Revenue',
      // Notifications
      notifications: 'Notifications',
      newBookingReceived: 'New Booking Received',
      bookingCancelled: 'Booking Cancelled',
      bookingUpdated: 'Booking Updated',
      syncFailed: 'Sync Failed',
      noNotifications: 'No notifications',
      markAllRead: 'Mark all read',
      // Offline
      youAreOffline: 'You are offline',
      backOnline: 'Back online. Syncing latest data...',
      offlineDraft: 'Offline Draft',
      draftsSaved: 'drafts saved',
      // Errors
      serverUnavailable: 'Server temporarily unavailable',
      unableToLoad: 'Unable to load data',
      syncRetrying: 'Sync failed. Retrying...',
      requestTimeout: 'Request timed out. Please try again.',
      networkError: 'Network error. Check your connection.',
      unauthorized: 'Session expired. Please login again.',
      serverError: 'Something went wrong. Please try again.',
      // Settings
      language: 'Language',
      english: 'English',
      hindi: 'Hindi',
      notificationsToggle: 'Notifications',
      soundToggle: 'Sound',
      autoSync: 'Auto Sync',
      offlineSyncStatus: 'Offline Sync Status',
      on: 'On',
      off: 'Off',
      // Profile
      propertyDetails: 'Property Details',
      switchProperty: 'Switch Property',
      // Misc
      today: 'Today',
      noData: 'No data available',
      loading: 'Loading...',
      search: 'Search',
      filter: 'Filter',
      all: 'All',
      nights: 'Nights',
      guests: 'Guests',
      rooms: 'Rooms',
      adults: 'Adults',
      children: 'Children',
    },
  },
  hi: {
    translation: {
      // Navigation
      calendar: 'कैलेंडर',
      analytics: 'एनालिटिक्स',
      profile: 'प्रोफ़ाइल',
      settings: 'सेटिंग्स',
      // Booking
      bookings: 'बुकिंग्स',
      addBooking: 'बुकिंग जोड़ें',
      newBooking: 'नई बुकिंग',
      checkIn: 'चेक-इन',
      checkOut: 'चेक-आउट',
      guestName: 'मेहमान का नाम',
      roomType: 'कमरे का प्रकार',
      amount: 'राशि',
      status: 'स्थिति',
      confirmed: 'कन्फ़र्म',
      cancelled: 'रद्द',
      checkedIn: 'चेक-इन हुआ',
      checkedOut: 'चेक-आउट हुआ',
      pending: 'पेंडिंग',
      // Actions
      save: 'सेव करें',
      cancel: 'रद्द करें',
      delete: 'हटाएं',
      edit: 'संपादित करें',
      retry: 'पुनः प्रयास करें',
      close: 'बंद करें',
      logout: 'लॉगआउट',
      login: 'लॉगिन',
      // Dashboard / Analytics
      totalBookings: 'कुल बुकिंग्स',
      totalRevenue: 'कुल आय',
      otaBookings: 'OTA बुकिंग्स',
      walkInBookings: 'वॉक-इन',
      cancelledBookings: 'रद्द',
      averageValue: 'औसत मूल्य',
      revenue: 'आय',
      // Notifications
      notifications: 'सूचनाएं',
      newBookingReceived: 'नई बुकिंग प्राप्त',
      bookingCancelled: 'बुकिंग रद्द हुई',
      bookingUpdated: 'बुकिंग अपडेट',
      syncFailed: 'सिंक विफल',
      noNotifications: 'कोई सूचना नहीं',
      markAllRead: 'सभी पढ़ें',
      // Offline
      youAreOffline: 'आप ऑफ़लाइन हैं',
      backOnline: 'वापस ऑनलाइन। डेटा सिंक हो रहा है...',
      offlineDraft: 'ऑफ़लाइन ड्राफ़्ट',
      draftsSaved: 'ड्राफ़्ट सहेजे गए',
      // Errors
      serverUnavailable: 'सर्वर अस्थायी रूप से अनुपलब्ध है',
      unableToLoad: 'डेटा लोड नहीं हो सका',
      syncRetrying: 'सिंक विफल। पुनः प्रयास हो रहा है...',
      requestTimeout: 'अनुरोध समय-सीमित। कृपया पुनः प्रयास करें।',
      networkError: 'नेटवर्क त्रुटि। अपना कनेक्शन जांचें।',
      unauthorized: 'सत्र समाप्त। कृपया फिर से लॉगिन करें।',
      serverError: 'कुछ गलत हुआ। कृपया पुनः प्रयास करें।',
      // Settings
      language: 'भाषा',
      english: 'English',
      hindi: 'हिंदी',
      notificationsToggle: 'सूचनाएं',
      soundToggle: 'ध्वनि',
      autoSync: 'ऑटो सिंक',
      offlineSyncStatus: 'ऑफ़लाइन सिंक स्थिति',
      on: 'चालू',
      off: 'बंद',
      // Profile
      propertyDetails: 'प्रॉपर्टी विवरण',
      switchProperty: 'प्रॉपर्टी बदलें',
      // Misc
      today: 'आज',
      noData: 'कोई डेटा उपलब्ध नहीं',
      loading: 'लोड हो रहा है...',
      search: 'खोजें',
      filter: 'फ़िल्टर',
      all: 'सभी',
      nights: 'रातें',
      guests: 'मेहमान',
      rooms: 'कमरे',
      adults: 'वयस्क',
      children: 'बच्चे',
    },
  },
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: localStorage.getItem('superhost_lang') || 'en',
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
  });

export default i18n;
